import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

const TEMPLATE = "slug,name,category,description,price,image,images,colors,sizes,gift_options\nphoto-mug,Personalised Photo Mug,Mugs & Tumblers,Custom photo mug,,https://example.com/mug.jpg,,White|Black,,Name [required]; Message [textarea]; Colour [select: Red|Blue|Black]\n";

function parseGiftOptions(value) {
  const raw = String(value || "").trim();
  if (!raw) return [];
  return raw.split(";").map(entry => {
    const directives = [];
    const label = entry.replace(/\s*\[[^\]]+\]\s*/g, directive => {
      directives.push(directive.slice(directive.indexOf("[") + 1, directive.lastIndexOf("]")).trim());
      return " ";
    }).trim();
    const selectDirective = directives.find(directive => /^select\s*:/i.test(directive));
    const type = selectDirective ? "select" : directives.some(directive => /^textarea$/i.test(directive)) ? "textarea" : "text";
    const choices = selectDirective ? selectDirective.replace(/^select\s*:/i, "").split("|").map(choice => choice.trim()) : [];
    const name = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    return { name, label, type, required: directives.some(directive => /^required$/i.test(directive)), ...(type === "select" ? { choices } : {}) };
  });
}

function parseCSV(text) {
  const rows = [];
  let row = [], cell = "", quoted = false, afterQuote = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], next = text[i + 1];
    if (quoted && ch === '"' && next === '"') { cell += '"'; i++; }
    else if (quoted && ch === '"') { quoted = false; afterQuote = true; }
    else if (afterQuote && ch !== "," && ch !== "\n" && ch !== "\r" && !/\s/.test(ch)) {
      throw new Error("CSV contains unexpected text after a closing quote. Put the entire field inside quotes and export again.");
    }
    else if (afterQuote && (ch === " " || ch === "\t")) {
      // Ignore harmless whitespace between a closing quote and the delimiter.
    }
    else if (ch === '"' && !quoted && cell.trim() !== "") {
      throw new Error("CSV contains a quote inside an unquoted field. Quote the entire field in Excel and export again.");
    }
    else if (ch === '"' && !quoted) { quoted = true; }
    else if (ch === "," && !quoted) { row.push(cell.trim()); cell = ""; afterQuote = false; }
    else if ((ch === "\n" || ch === "\r") && !quoted) {
      if (ch === "\r" && next === "\n") i++;
      row.push(cell.trim());
      if (row.some(value => value !== "")) rows.push(row);
      row = []; cell = ""; afterQuote = false;
    } else cell += ch;
  }
  if (quoted) throw new Error("CSV contains an unclosed quoted field.");
  if (cell || row.length) {
    row.push(cell.trim());
    if (row.some(value => value !== "")) rows.push(row);
  }
  if (!rows.length) return [];
  const headers = rows.shift().map(value => value.replace(/^\uFEFF/, "").trim().toLowerCase());
  if (headers.some(header => !header)) throw new Error("CSV contains an empty column heading.");
  if (new Set(headers).size !== headers.length) throw new Error("CSV contains duplicate column headings.");
  return rows.map((values, index) => {
    if (values.length !== headers.length) {
      throw new Error("CSV row " + (index + 2) + " has " + values.length + " columns; expected " + headers.length + ".");
    }
    return Object.fromEntries(headers.map((key, i) => [key, values[i] || ""]));
  });
}

export default function AdminProductBulkImport({ onImported }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const fileReadVersion = useRef(0);
  const issues = useMemo(() => {
    const errors = [], seen = new Set();
    rows.forEach((row, i) => {
      const line = i + 2;
      const slug = String(row.slug || "").trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
      if (!slug || !String(row.name || "").trim() || !String(row.category || "").trim()) errors.push("Row " + line + ": slug, name and category are required.");
      if (slug && seen.has(slug)) errors.push("Row " + line + ": duplicate slug " + slug + ".");
      if (slug) seen.add(slug);
      if (String(row.price || "").trim() && (!Number.isFinite(Number(row.price)) || Number(row.price) <= 0)) errors.push("Row " + line + ": price must be blank or greater than zero.");
      const rawGiftOptions = String(row.gift_options || "").trim();
      if (rawGiftOptions && rawGiftOptions.split(";").some(entry => !entry.trim())) {
        errors.push("Row " + line + ": gift option fields cannot be empty; remove extra semicolons.");
      }
      if (rawGiftOptions && rawGiftOptions.split(";").some(entry => {
        const directives = entry.match(/\[[^\]]*\]/g) || [];
        const remaining = entry.replace(/\[[^\]]*\]/g, "");
        if (remaining.includes("[") || remaining.includes("]")) return true;
        const typeDirectives = directives.filter(match => {
          const directive = match.slice(1, -1).trim();
          return /^textarea$/i.test(directive) || /^select\s*:/i.test(directive);
        });
        if (typeDirectives.length > 1) return true;
        return directives.some(match => {
          const directive = match.slice(1, -1).trim();
          return !/^required$/i.test(directive)
            && !/^textarea$/i.test(directive)
            && !/^select\s*:/i.test(directive);
        });
      })) {
        errors.push("Row " + line + ": unsupported, malformed or conflicting gift option directives. Use only one field type: [textarea] or [select: Red|Blue], optionally with [required].");
      }
      const giftOptions = parseGiftOptions(row.gift_options);
      if (giftOptions.length > 20) errors.push("Row " + line + ": use at most 20 gift options.");
      if (giftOptions.some(option => !option.label || !option.name)) errors.push("Row " + line + ": each gift option needs a valid, non-empty label using letters or numbers.");
      const optionNames = giftOptions.map(option => option.name.toLowerCase()).filter(Boolean);
      if (new Set(optionNames).size !== optionNames.length) errors.push("Row " + line + ": gift option labels must be unique.");
      if (giftOptions.some(option => option.type === "select" && !option.choices.length)) errors.push("Row " + line + ": select gift options need at least one choice.");
      if (giftOptions.some(option => option.type === "select" && option.choices.some(choice => !choice))) errors.push("Row " + line + ": dropdown choices cannot be empty (check for consecutive or trailing | characters).");
      if (giftOptions.some(option => option.type === "select" && new Set(option.choices.map(choice => choice.toLowerCase())).size !== option.choices.length)) errors.push("Row " + line + ": dropdown choices must be unique.");
      if (giftOptions.some(option => option.type === "select" && option.choices.length > 30)) errors.push("Row " + line + ": each dropdown can have at most 30 choices.");
      const images = Array.from(new Set([row.image].concat(String(row.images || "").split("|")).map(v => String(v || "").trim()).filter(Boolean)));
      if (images.length > 5) errors.push("Row " + line + ": use at most 5 image URLs in total.");
      images.forEach(value => {
        try {
          const parsed = new URL(value);
          if (!["http:", "https:"].includes(parsed.protocol) || !parsed.hostname || parsed.username || parsed.password) {
            errors.push("Row " + line + ": image values must be valid HTTP(S) URLs.");
          }
        } catch { errors.push("Row " + line + ": image values must be valid HTTP(S) URLs."); }
      });
    });
    return errors;
  }, [rows]);
  const onFile = async event => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const readVersion = ++fileReadVersion.current;
    // Clear the old preview immediately so a failed new selection can never
    // leave the previous file available for accidental import.
    setRows([]);
    setFileName("");
    try {
      if (!/\.csv$/i.test(file.name)) throw new Error("Upload a CSV file exported from Excel.");
      if (file.size > 5 * 1024 * 1024) throw new Error("CSV must be 5 MB or smaller.");
      const fileText = await file.text();
      if (readVersion !== fileReadVersion.current) return;
      const parsed = parseCSV(fileText);
      if (!parsed.length || ["slug", "name", "category"].some(key => !(key in parsed[0]))) {
        throw new Error("CSV must include slug, name and category columns.");
      }
      if (parsed.length > 500) throw new Error("Import up to 500 products per file.");
      setRows(parsed);
      setFileName(file.name);
      toast.success("Loaded " + parsed.length + " rows for preview.");
    } catch (error) {
      if (readVersion === fileReadVersion.current) {
        setRows([]);
        setFileName("");
        toast.error(error.message || "Could not read CSV.");
      }
    } finally {
      event.target.value = "";
    }
  };
  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a"); link.href = url; link.download = "printmytees-gift-import-template.csv"; link.click(); URL.revokeObjectURL(url);
  };
  const importRows = async () => {
    if (!rows.length || issues.length || busy) return;
    setBusy(true);
    try {
      const payload = rows.map(row => ({
        ...row, price: String(row.price || "").trim() ? Number(row.price) : null,
        images: Array.from(new Set([row.image].concat(String(row.images || "").split("|")).map(v => String(v || "").trim()).filter(Boolean))),
        colors: String(row.colors || "").split("|").map(v => v.trim()).filter(Boolean),
        sizes: String(row.sizes || "").split("|").map(v => v.trim()).filter(Boolean),
        gift_options: parseGiftOptions(row.gift_options)
      }));
      const response = await api.post("/admin/products/bulk-gifts", { rows: payload });
      // The server has created the drafts. Clear the import preview before
      // refreshing the catalogue so a refresh failure cannot invite a duplicate retry.
      setRows([]);
      setFileName("");
      toast.success(response.data.message || ("Imported " + (response.data.created || rows.length) + " drafts."));
      if (onImported) {
        try {
          await onImported();
        } catch {
          toast.error("Drafts were imported, but the product list could not refresh. Reload the Products page.");
        }
      }
    } catch (error) {
      const detail = error && error.response && error.response.data && error.response.data.detail;
      const message = typeof detail === "string" ? detail : (detail && detail.message) || "Import failed. No products were saved.";
      const duplicates = detail && detail.duplicate_slugs;
      const rowErrors = detail && Array.isArray(detail.errors) ? detail.errors : [];
      toast.error(duplicates && duplicates.length
        ? "Already exist: " + duplicates.join(", ")
        : rowErrors.length ? rowErrors.slice(0, 3).join("; ") : message);
    } finally { setBusy(false); }
  };
  return <section className="container-pmt mt-6" data-testid="gift-bulk-import">
    <div className="card-pmt p-4">
      <div className="flex flex-wrap items-start justify-between gap-3"><div><div className="overline">Faster catalogue setup</div><h2 className="text-xl font-bold mt-1">Bulk Import Gift Products</h2><p className="text-sm text-stone-600 mt-1">Upload a CSV exported from Excel. Imported items are inactive drafts and cannot be purchased until reviewed and published.</p></div>
        <button type="button" className="btn-outline-pmt inline-flex items-center gap-2" onClick={downloadTemplate}><Download size={15}/> Download CSV template</button></div>
      <div className="mt-4 flex flex-wrap items-center gap-3"><label className="btn-pmt inline-flex cursor-pointer items-center gap-2"><FileUp size={16}/> Choose CSV<input type="file" accept=".csv,text/csv" className="hidden" disabled={busy} onChange={onFile}/></label>{fileName && <span className="text-sm text-stone-600">{fileName} · {rows.length} rows</span>}{rows.length > 0 && <button type="button" className="btn-outline-pmt" disabled={busy} onClick={() => {setRows([]);setFileName("");}}>Clear preview</button>}</div>
      {rows.length > 0 && <div className="mt-4"><div className="flex items-center gap-2 text-sm font-semibold">{issues.length ? <AlertTriangle size={16} className="text-amber-600"/> : <CheckCircle2 size={16} className="text-green-700"/>}{issues.length ? issues.length + " issue(s) to fix before import" : rows.length + " rows ready for draft import"}</div>
        {issues.length > 0 && <ul className="mt-2 max-h-32 overflow-auto list-disc pl-5 text-xs text-red-700">{issues.slice(0,30).map((issue,i)=><li key={i}>{issue}</li>)}</ul>}
        <div className="mt-3 overflow-x-auto"><table className="w-full text-xs border-collapse"><thead><tr>{["Slug","Name","Category","Price","Status"].map(label=><th key={label} className="text-left border-b p-2">{label}</th>)}</tr></thead><tbody>{rows.slice(0,10).map((row,i)=><tr key={i}><td className="border-b p-2">{row.slug}</td><td className="border-b p-2">{row.name}</td><td className="border-b p-2">{row.category}</td><td className="border-b p-2">{String(row.price || "").trim() ? "₹" + row.price : "Get Quote"}</td><td className="border-b p-2">Inactive draft</td></tr>)}</tbody></table>{rows.length > 10 && <p className="text-xs text-stone-500 mt-2">Showing first 10 of {rows.length} rows.</p>}</div>
        <button type="button" className="btn-pmt mt-4" disabled={busy || issues.length > 0} onClick={importRows}>{busy ? "Importing…" : "Import " + rows.length + " gift drafts"}</button>
      </div>}
      <p className="text-xs text-stone-500 mt-3">Columns: slug, name, category, description, price, image, images, colors, sizes, gift_options. Separate images, colours and sizes with |. Separate gift fields with ; and use [required], [textarea] or [select: Red|Blue] directives. Leave price blank for Get Quote. Up to 500 rows per file. Save Excel files as CSV first.</p>
    </div>
  </section>;
}
